"use client"

import { useEffect, useRef } from "react"

import type { HighlighterProps } from "@/components/ui/highlighter"

// Upper bound on how long the idle scheduler may hold the annotation back.
const HIGHLIGHT_IDLE_TIMEOUT_MS = 2000
// Fallback delay for browsers without requestIdleCallback (Safari).
const HIGHLIGHT_FALLBACK_MS = 800

/**
 * Renders the text in its final box from the server, then draws the
 * rough-notation annotation over it once the browser is idle. The hero headline
 * is the LCP element, so it must not wait on rough-notation or on hydration to
 * paint, and the wrapper never changes, so drawing the line shifts nothing.
 */
export function DeferredHighlighter({
  children,
  action = "highlight",
  color,
  strokeWidth,
  animationDuration,
  iterations,
  padding = 2,
  underlineGap,
  multiline = true,
}: HighlighterProps) {
  const elementRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    let cancelled = false
    let teardown: (() => void) | undefined

    const draw = async () => {
      const { annotate } = await import("rough-notation")
      if (cancelled) return
      const annotation = annotate(element, {
        type: action,
        color,
        strokeWidth,
        animationDuration,
        iterations,
        padding:
          action === "underline" && underlineGap !== undefined
            ? [padding, padding, underlineGap, padding]
            : padding,
        multiline,
      })
      annotation.show()
      const resizeObserver = new ResizeObserver(() => {
        annotation.hide()
        annotation.show()
      })
      resizeObserver.observe(element)
      resizeObserver.observe(document.body)
      teardown = () => {
        resizeObserver.disconnect()
        annotation.remove()
      }
    }

    let cancelSchedule: () => void
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(() => void draw(), {
        timeout: HIGHLIGHT_IDLE_TIMEOUT_MS,
      })
      cancelSchedule = () => window.cancelIdleCallback(id)
    } else {
      const id = window.setTimeout(() => void draw(), HIGHLIGHT_FALLBACK_MS)
      cancelSchedule = () => window.clearTimeout(id)
    }

    return () => {
      cancelled = true
      cancelSchedule()
      teardown?.()
    }
  }, [
    action,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    underlineGap,
    multiline,
  ])

  return (
    <span ref={elementRef} className="relative inline-block">
      {children}
    </span>
  )
}
